import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon, IconVariantTypes } from '../Icon';
import { Tooltip } from '../Tooltip';
import {
  actionMenuContainerStyles,
  menuDividerStyles,
  menuDropdownStyles,
  menuItemStyles,
  triggerButtonStyles,
} from './ActionMenu.styles';

export type ActionMenuItem = {
  /**
   * Label for the action
   */
  label: string;
  /**
   * Icon to display next to the label (optional)
   */
  icon?: IconVariantTypes;
  /**
   * Click handler for the action
   */
  onClick: () => void;
  /**
   * Whether this is a destructive action (will be styled in red)
   */
  danger?: boolean;
  /**
   * Whether the action is disabled
   */
  disabled?: boolean;
  /**
   * Tooltip to show on hover (optional)
   */
  tooltip?: string;
};

export type ActionMenuProps = {
  /**
   * Array of action items to display in the menu
   */
  actions: (ActionMenuItem | 'divider')[];
  /**
   * Tooltip content for the trigger button
   */
  triggerTooltip?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * ARIA label for the trigger button
   */
  ariaLabel?: string;
};

const ActionMenu = ({
  actions,
  triggerTooltip = '',
  className,
  ariaLabel = 'Open actions menu',
}: ActionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (wrapperRef.current && isOpen) {
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const menuWidth = menuRef.current?.offsetWidth || 160;

        setMenuPosition({
          top: wrapperRect.bottom + 4,
          left: wrapperRect.right - menuWidth,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: ActionMenuItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  const menuContent = (
    <div
      ref={menuRef}
      css={menuDropdownStyles}
      style={{
        position: 'fixed',
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }}
      data-visible={isOpen}
      role="menu"
      aria-hidden={!isOpen}
    >
      {actions.map((item, index) => {
        if (item === 'divider') {
          return (
            <div
              key={`divider-${index}`}
              css={menuDividerStyles}
              role="separator"
            />
          );
        }

        const button = (
          <button
            key={`action-${index}`}
            css={menuItemStyles}
            onClick={(e) => handleActionClick(item, e)}
            data-danger={item.danger}
            disabled={item.disabled}
            role="menuitem"
            type="button"
          >
            {item.icon && <Icon variant={item.icon} size="small" />}
            {item.label}
          </button>
        );

        if (item.tooltip && !item.disabled) {
          return (
            <Tooltip
              key={`action-${index}`}
              content={item.tooltip}
              position="left"
            >
              {button}
            </Tooltip>
          );
        }

        return button;
      })}
    </div>
  );

  const triggerButton = (
    <button
      ref={buttonRef}
      css={triggerButtonStyles}
      onClick={handleToggle}
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      aria-haspopup="true"
      type="button"
    >
      <Icon variant="Ellipsis" size="small" />
    </button>
  );

  return (
    <>
      <div css={actionMenuContainerStyles} className={className}>
        <div
          ref={wrapperRef}
          style={{ position: 'relative', display: 'inline-block' }}
        >
          {triggerTooltip ? (
            <Tooltip content={triggerTooltip} position="top">
              {triggerButton}
            </Tooltip>
          ) : (
            triggerButton
          )}
        </div>
      </div>
      {typeof document !== 'undefined' &&
        createPortal(menuContent, document.body)}
    </>
  );
};

ActionMenu.displayName = 'ActionMenu';

export default ActionMenu;
